"use client";

import { Api } from "@/app/api";
import {
	Order,
	OrderAggregated,
	OrderCollection,
	OrderCreate,
	OrderItem,
	OrderProgress,
	OrderStatus,
} from "@/types/orders";

export const orderInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getOrder: builder.query<Order, string>({
			query: (id) => ({
				url: `/orders/${id}`,

				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["order"],
		}),
		getOrders: builder.query<
			OrderCollection,
			{
				userId?: string;
				page?: string | number;
				limit?: string | number;
				purchaseDateTo?: Date;
				purchaseDateFrom?: Date;
				orderStatus?: OrderStatus;
				pagination?: boolean;
			}
		>({
			query: ({
				userId,
				page,
				limit,
				purchaseDateTo,
				purchaseDateFrom,
				orderStatus,
				pagination = true,
			}) => ({
				url: `/orders`,
				params: {
					pagination,
					userId,
					page,
					limit,
					purchaseDateTo,
					purchaseDateFrom,
					orderStatus,
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["order"],
		}),
		getOrdersAggregated: builder.query<OrderAggregated[], void>({
			query: () => ({
				url: `/orders/aggregated-view`,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["order"],
		}),
		getHostingUsage: builder.query<
			{
				purchasedHostingEligibleServiceCount: number;
				activeHostingTier: "starter" | "growth" | "pro" | null;
			},
			void
		>({
			query: () => ({
				// GET — omit Content-Type so Safari does not force a CORS preflight.
				url: `/orders/hosting-usage`,
			}),
			providesTags: ["order"],
		}),
		searchOrders: builder.query<
			OrderCollection,
			{
				searchQuery?: string;
				sortBy?: string;
				page: number;
				limit: number;
				categoryId?: string;
				orderStatus?: OrderStatus;
			}
		>({
			query: ({ searchQuery, sortBy, page, limit, categoryId, orderStatus }) => ({
				url: `/orders`,
				params: {
					page: page,
					limit: limit,
					search: searchQuery,
					sortBy: sortBy,
					categoryId: categoryId,
					orderStatus,
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["order"],
		}),
		createOrder: builder.mutation<{ stripeUrl: string }, OrderCreate>({
			query: (basket) => ({
				url: "/orders",
				method: "POST",
				body: basket,
				headers: {
					"Content-Type": "application/json",
					...(basket.currency && { "x-currency": basket.currency }),
				},
			}),
			invalidatesTags: ["order"],
		}),
		createGuestOrder: builder.mutation<{ stripeUrl: string }, OrderCreate>({
			query: (basket) => ({
				url: "/orders",
				method: "POST",
				body: { ...basket, guestCheckout: true },
				headers: {
					"Content-Type": "application/json",
					"X-Requested-With": "XMLHttpRequest",
					...(basket.currency && { "x-currency": basket.currency }),
				},
			}),
			invalidatesTags: ["order"],
		}),
		getGuestOrderConfirmation: builder.query<
			{
				status: "paid" | "pending" | "unpaid";
				email?: string;
				accountCreated?: boolean;
				needsAccountSetup?: boolean;
				amount?: number;
				currency?: string;
			},
			{ orderId: string; sessionId: string }
		>({
			query: ({ orderId, sessionId }) => ({
				url: `/orders/${orderId}/guest-confirmation`,
				params: { session_id: sessionId },
			}),
		}),
		completeGuestCheckoutPassword: builder.mutation<
			{ id_token: string; user: { id: string; email?: string } },
			{
				orderId: string;
				sessionId: string;
				password: string;
				confirmPassword: string;
			}
		>({
			query: ({ orderId, sessionId, password, confirmPassword }) => ({
				url: `/orders/${orderId}/guest-complete-password`,
				method: "POST",
				body: {
					session_id: sessionId,
					password,
					confirmPassword,
				},
				headers: {
					"Content-Type": "application/json",
					"X-Requested-With": "XMLHttpRequest",
				},
			}),
		}),
		completeGuestCheckoutSso: builder.mutation<
			{ id_token: string; user: { id: string; email?: string } },
			{
				orderId: string;
				sessionId: string;
				sso: "google" | "apple";
				code?: string;
				token?: string;
			}
		>({
			query: ({ orderId, sessionId, sso, code, token }) => ({
				url: `/orders/${orderId}/guest-complete-sso`,
				method: "POST",
				body: {
					session_id: sessionId,
					sso,
					...(code ? { code } : {}),
					...(token ? { token } : {}),
				},
				headers: {
					"Content-Type": "application/json",
					"X-Requested-With": "XMLHttpRequest",
				},
			}),
		}),
		patchOrder: builder.mutation<void, { id: string; body: any }>({
			query: ({ id, body }) => ({
				url: `/orders/${id}`,
				method: "PATCH",
				body: body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["order"],
		}),
		submitOrderDomainSetup: builder.mutation<
			Order,
			{ orderId: string; body: { domain?: string; defer?: boolean } }
		>({
			query: ({ orderId, body }) => ({
				url: `/orders/${orderId}/domain-setup`,
				method: "POST",
				body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["order"],
		}),
		patchItem: builder.mutation<
			void,
			{
				orderId: string;
				itemId: string;
				body: { progress?: string; noLongerNeedsHosting?: boolean };
			}
		>({
			query: ({ orderId, itemId, body }) => ({
				url: `/orders/${orderId}/sub-orders/${itemId}`,
				method: "PATCH",
				body: body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["order"],
			// Optimistically update the cache for immediate UI feedback
			async onQueryStarted({ orderId, itemId, body }, { dispatch, queryFulfilled, getState }) {
				// Optimistically update cached getOrders queries
				// We need to find and update all cached queries regardless of their parameters
				const patchResults: Array<{ undo: () => void }> = [];
				
				// Get the RTK Query cache state
				const state = getState();
				const apiState = (state as any).api;
				
				if (apiState?.queries) {
					// Find all getOrders queries in the cache
					Object.entries(apiState.queries).forEach(([queryKey, queryState]: [string, any]) => {
						// RTK Query serializes query keys like: "getOrders({...})"
						if (queryKey.includes('getOrders(') && queryState?.data) {
							try {
								// Extract query params from the serialized key
								const paramsMatch = queryKey.match(/getOrders\((.+)\)/);
								if (paramsMatch) {
									const params = JSON.parse(paramsMatch[1]) as {
										userId?: string;
										page?: string | number;
										limit?: string | number;
										purchaseDateTo?: Date;
										purchaseDateFrom?: Date;
										orderStatus?: OrderStatus;
										pagination?: boolean;
									};
									
									// Update this specific query
									const patchResult = dispatch(
										(Api.util.updateQueryData as any)('getOrders', params, (draft: any) => {
											if (draft?.docs) {
												for (const order of draft.docs) {
													if (order.id === orderId) {
														const item = order.items.find((item: OrderItem) => item._id === itemId);
														if (item) {
															if (body.progress) {
																item.progress = body.progress as OrderProgress;
															}
															if (typeof body.noLongerNeedsHosting === "boolean") {
																item.noLongerNeedsHosting = body.noLongerNeedsHosting;
															}
														}
													}
												}
											}
										})
									);
									if (patchResult) {
										patchResults.push(patchResult);
									}
								}
							} catch (e) {
								// If parsing fails, skip this query
							}
						}
					});
				}
				
				// Also update getOrder query if it exists
				try {
					const orderPatchResult = dispatch(
						(Api.util.updateQueryData as any)('getOrder', orderId, (draft: any) => {
							if (draft?.items) {
								const item = draft.items.find((item: OrderItem) => item._id === itemId);
								if (item) {
									if (body.progress) {
										item.progress = body.progress as OrderProgress;
									}
									if (typeof body.noLongerNeedsHosting === "boolean") {
										item.noLongerNeedsHosting = body.noLongerNeedsHosting;
									}
								}
							}
						})
					);
					if (orderPatchResult) {
						patchResults.push(orderPatchResult);
					}
				} catch (e) {
					// Query might not exist, skip silently
				}
				
				try {
					await queryFulfilled;
					if (typeof body.noLongerNeedsHosting === "boolean") {
						dispatch(
							orderInjection.endpoints.getHostingUsage.initiate(undefined, {
								forceRefetch: true,
							}),
						);
					}
					// Success - cache invalidation will refetch fresh data to ensure consistency
				} catch {
					// If the mutation fails, revert all optimistic updates
					patchResults.forEach((result) => result.undo());
				}
			},
		}),
		getItem: builder.query<OrderItem, { orderId: string; itemId: string }>({
			query: ({ orderId, itemId }) => ({
				url: `/orders/${orderId}/sub-orders/${itemId}`,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["order"],
		}),
		getReceiptUrl: builder.query<
			import("@/lib/order/order-receipts").OrderReceiptsResponse,
			string
		>({
			query: (orderId) => ({
				url: `/orders/${orderId}/receipt`,
			}),
		}),
		syncHostingPlan: builder.mutation<{ ok: boolean }, void>({
			query: () => ({
				url: `/orders/sync-hosting-plan`,
				method: "POST",
			}),
			invalidatesTags: ["user", "order"],
		}),
	}),
});

export const {
	useCreateOrderMutation,
	useCreateGuestOrderMutation,
	useLazyGetGuestOrderConfirmationQuery,
	useCompleteGuestCheckoutPasswordMutation,
	useCompleteGuestCheckoutSsoMutation,
	usePatchOrderMutation,
	useSubmitOrderDomainSetupMutation,
	usePatchItemMutation,
	useGetOrdersQuery,
	useGetOrdersAggregatedQuery,
	useGetHostingUsageQuery,
	useGetOrderQuery,
	useSearchOrdersQuery,
	useGetItemQuery,
	useGetReceiptUrlQuery,
	useLazyGetReceiptUrlQuery,
	useSyncHostingPlanMutation,
} = orderInjection;
